import type { TelemetryClient } from "./client";
import { stripQuery } from "./sanitize";

function getElementLabel(el: HTMLElement): string {
  // Prefer data-track attribute for explicit labeling
  const track = el.getAttribute("data-track");
  if (track) return track;

  // Aria label
  const aria = el.getAttribute("aria-label");
  if (aria) return aria;

  // Text content (truncated)
  const text = el.textContent?.trim();
  if (text && text.length <= 50) return text;
  if (text) return text.slice(0, 47) + "...";

  // Tag + id/class fallback
  const id = el.id ? `#${el.id}` : "";
  const cls = el.className && typeof el.className === "string"
    ? `.${el.className.split(" ").slice(0, 2).join(".")}`
    : "";
  return `${el.tagName.toLowerCase()}${id}${cls}`;
}

export function trackInteractions(client: TelemetryClient): () => void {
  if (typeof document === "undefined") return () => {};

  // Click tracking via event delegation
  const clickHandler = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    // Walk up to find the nearest interactive element
    const interactive = target.closest("a, button, [role='button'], [data-track], input[type='submit']") as HTMLElement | null;
    if (!interactive) return;

    const label = getElementLabel(interactive);
    // Origin + path only: a link can carry a token in its query string.
    const href = interactive instanceof HTMLAnchorElement ? stripQuery(interactive.href) : undefined;

    client.track("interaction", {
      action: "click",
      element: interactive.tagName.toLowerCase(),
      label,
      href,
    });
  };

  // Form submission tracking
  const submitHandler = (e: SubmitEvent) => {
    const form = e.target as HTMLFormElement | null;
    if (!form) return;

    client.track("interaction", {
      action: "submit",
      element: "form",
      label: form.getAttribute("data-track") || form.id || stripQuery(form.action),
    });
  };

  // Scroll depth tracking
  const scrollThresholds = new Set<number>();
  const scrollHandler = () => {
    const scrollPercent = Math.round(
      (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
    );

    for (const threshold of [25, 50, 75, 100]) {
      if (scrollPercent >= threshold && !scrollThresholds.has(threshold)) {
        scrollThresholds.add(threshold);
        client.track("interaction", {
          action: "scroll",
          element: "page",
          label: `${threshold}%`,
          depth: threshold,
        });
      }
    }
  };

  document.addEventListener("click", clickHandler, { passive: true });
  document.addEventListener("submit", submitHandler, { passive: true });
  window.addEventListener("scroll", scrollHandler, { passive: true });

  return () => {
    document.removeEventListener("click", clickHandler);
    document.removeEventListener("submit", submitHandler);
    window.removeEventListener("scroll", scrollHandler);
  };
}
